import { useCallback, useEffect, useMemo, useId } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import partialRight from 'lodash/partialRight';
import { PapiRequestDaSharedUserCalculateEarnings } from '@wix/da-papi-types';
import {
  getEstimatedEarnings,
  getServiceFeePercent,
  getIsDisplayInitialized,
  getIsReduxLoaded,
  getRequestInfo,
  getActualPrice,
} from './redux/selectors';
import {
  destroyEarningsDisplay,
  initializeEarningsDisplay,
  updatePrice,
} from './redux/actions';

function useDisplaySpecificSelector<T>(
  displayId: string,
  selector: (state, displayId: string) => T
) {
  const displaySpecificSelector = partialRight(selector, displayId);
  return useSelector(displaySpecificSelector);
}

function useInitializationEffect(
  displayId: string,
  productType: PapiRequestDaSharedUserCalculateEarnings['product_type'],
  isResale: boolean,
  price: number
) {
  const dispatch = useDispatch();
  const isReduxLoaded = useSelector(getIsReduxLoaded);
  const isDisplayInitialized = useDisplaySpecificSelector(
    displayId,
    getIsDisplayInitialized
  );

  useEffect(() => {
    if (isReduxLoaded && !isDisplayInitialized && productType) {
      dispatch(
        initializeEarningsDisplay(displayId, productType, isResale, price)
      );
    }
  }, [
    dispatch,
    displayId,
    productType,
    isReduxLoaded,
    price,
    isDisplayInitialized,
    isResale,
  ]);
}

function useDebouncedPriceUpdateEffect(displayId: string, price: number) {
  const dispatch = useDispatch();

  const isReduxLoaded = useSelector(getIsReduxLoaded);
  const isDisplayInitialized = useDisplaySpecificSelector(
    displayId,
    getIsDisplayInitialized
  );
  const priceStoredInRedux = useDisplaySpecificSelector(
    displayId,
    getActualPrice
  );

  const dispatchUpdatePrice = useCallback(
    (storedPrice: number | null, incomingPrice: number) => {
      if (
        isReduxLoaded &&
        isDisplayInitialized &&
        // When we initialize the display, we also fire a request to the backend.
        // This check makes sure we don't double up on that request.
        storedPrice !== incomingPrice
      ) {
        dispatch(updatePrice(displayId, incomingPrice));
      }
    },
    [dispatch, displayId, isReduxLoaded, isDisplayInitialized]
  );

  const debouncedUpdatePrice = useMemo(
    () => debounce(dispatchUpdatePrice, 500),
    [dispatchUpdatePrice]
  );

  useEffect(() => {
    debouncedUpdatePrice(priceStoredInRedux, price);
    return () => {
      debouncedUpdatePrice.cancel();
    };
  }, [debouncedUpdatePrice, price, priceStoredInRedux]);
}

function useCleanupEffect(displayId: string) {
  const dispatch = useDispatch();
  useEffect(() => {
    return () => {
      dispatch(destroyEarningsDisplay(displayId));
    };
  }, [dispatch, displayId]);
}

export function useEstimatedEarningsDisplay(
  price: number,
  productType: PapiRequestDaSharedUserCalculateEarnings['product_type'],
  isResale: boolean = false
) {
  const displayId = useId();

  useInitializationEffect(displayId, productType, isResale, price);
  useDebouncedPriceUpdateEffect(displayId, price);
  useCleanupEffect(displayId);

  const earnings = useDisplaySpecificSelector(displayId, getEstimatedEarnings);
  const serviceFeePercent = useDisplaySpecificSelector(
    displayId,
    getServiceFeePercent
  );
  const requestInfo = useDisplaySpecificSelector(displayId, getRequestInfo);

  return { displayId, earnings, serviceFeePercent, requestInfo };
}
